import { Badge, type BadgeProps } from "@/components/ui/badge";

/**
 * Maps a status string to a Badge variant so status presentation is uniform
 * across the app. Add new mappings here — features should NOT pass a raw
 * `variant` to Badge for status cells; reach for <StatusBadge value={...} />
 * instead.
 */

type StatusVariant = NonNullable<BadgeProps["variant"]>;

const STATUS_MAP: Record<string, StatusVariant> = {
  // Common renewal / pipeline statuses
  active: "success",
  healthy: "success",
  closed_won: "success",
  renewed: "success",
  // Reconciliation: a finished step + a signed-off month read as success.
  complete: "success",
  signed_off: "success",
  // Approval workflows (parent companies): a second user signed off / the AI
  // review came back clean.
  approved: "success",
  clear: "success",
  // Catalog: a package that can be added to a proposal.
  eligible: "success",
  // NEW logos earn the one SOLID status chip — a deliberate attention-grabber.
  // Everything else stays light-tinted (see the comment in badge.tsx).
  new: "success-solid",

  // Cash flow bank-file ingest: a week that landed cleanly, and a parsed file.
  ok: "success",
  parsed: "success",

  // Parent-companies relationship review: model raised at least one concern.
  flagged: "warning",

  // Cash flow ingest: some accounts missing, or a file no account claimed —
  // usable, but the week isn't complete.
  partial: "warning",
  unmatched: "warning",
  running: "warning",

  // Cash flow ingest: nothing landed, or a file the parser couldn't read.
  failed: "danger",

  // R-18 integration-expense verdicts. The severity split is "do we know WHY
  // this is wrong": a duplicate line or a churned customer is a defensible ask
  // to the vendor (danger); a dead feed or an unmapped ref is a question we
  // still have to answer ourselves (warning).
  duplicate: "danger",
  not_billing: "danger",
  cancellation_not_honoured: "danger",
  // Notice on record but still billing — softer than churned on purpose:
  // this is the window where cancelling the vendor in time saves money.
  cancelling: "warning",
  no_data: "warning",
  unmapped: "warning",
  count_delta: "warning",
  // R-18 vendor modes — how a vendor's invoice can be reconciled at all.
  line: "info",
  count: "warning",
  listed: "neutral",

  at_risk: "warning",
  // A continuing account whose ARR is shrinking — reuses the at-risk colors.
  decreasing: "warning",
  pending: "warning",
  in_progress: "warning",
  upcoming: "warning",

  // Approval workflows: reviewer declined the proposed change.
  rejected: "danger",

  churned: "danger",
  lost: "danger",
  closed_lost: "danger",
  overdue: "danger",
  expired: "danger",
  // Reconciliation: a step that can't proceed.
  blocked: "danger",

  draft: "neutral",
  inactive: "neutral",
  archived: "neutral",
  // Account folded into another — not a real loss, so a quiet terminal state.
  merged: "neutral",
  // Approval workflows: requester withdrew their own proposal.
  cancelled: "neutral",
  // Relationship roles (parent companies quick view chips). "stacked" marks a
  // multi-level chain in the grid's Depth column.
  parent: "info",
  child: "neutral",
  stacked: "info",
  // Reconciliation: a not-yet-opened month + a step marked not-applicable.
  not_started: "neutral",
  not_applicable: "neutral",

  // Integrations Monitor. The map is context-free by design, so the feature
  // passes STATE-QUALIFIED values: an incident row sends "critical" only
  // while it is OPEN (the one solid attention-grabber besides NEW logos);
  // resolved criticals send "critical_resolved" (tinted). New-entity kinds
  // stay tinted on purpose — the solid stays reserved.
  critical: "danger-solid",
  critical_resolved: "danger",
  alert: "warning",
  resolved: "success",
  on_time: "success",
  late: "warning",
  silent: "danger",
  insufficient_cadence_history: "neutral",
  dormant: "neutral",
  learning: "info",
  no_timely_signal: "neutral",
  went_quiet: "warning",
  step_change: "info",
  new_integration: "success",
  new_company: "success",
  new_location: "success",
  // Early-signal confidence — deliberately quiet (signals are the heads-up
  // tier, never the alarm): high caps at a warning tint.
  high: "warning",
  medium: "info",
  low: "neutral",
  confirmed: "success",
  cleared: "neutral",

  // Commission coverage on a live contract. The severity split is "is anybody
  // being paid at all": nobody assigned means the commission is simply not
  // happening (danger), whereas a missing role on an otherwise-assigned
  // contract reuses the existing `partial` warning. Both stay light-tinted —
  // the worklist opens with a long tail of these and a wall of solid chips
  // would read as 200 emergencies.
  unassigned: "danger",
  covered: "success",

  // Major Swag shipping. A box in transit is informational, a delivered one is
  // done, and an 'exception' is the only one worth chasing — kept tinted, not
  // solid, because a late blanket is not an outage.
  label_created: "info",
  in_transit: "info",
  delivered: "success",
  exception: "danger",
  unknown: "neutral",
  // Major Swag request decisions. 'denied' rather than 'rejected' because that
  // is the word the approval UI uses.
  denied: "danger",
  // Major Swag stock + provenance. NOT `unassigned` — that key is taken by
  // commission coverage above and means something else (nobody is being paid
  // at all, hence danger). This one is a giveaway with no department to
  // expense it to: it blocks Cody's posting, so warning, not danger.
  no_department: "warning",
  low_stock: "warning",
  out_of_stock: "neutral",
  imported: "neutral",
  group: "neutral",

  // Knowledge Base RAG. Pipeline run results: both "ok" flavours are a good
  // night; an incomplete crawl deleted nothing (warning, not danger); a
  // login failure is the one to act on but stays TINTED — the Overview banner
  // is the loud part, and a red column of them in Runs is history, not an
  // alarm.
  ok_no_changes: "success",
  incomplete_crawl: "warning",
  auth_failed: "danger",
  // Per-article sync status (articles.json).
  indexed: "success",
  changed_pending: "warning",
  pending_index: "warning",
  deleted_pending_purge: "neutral",
  // The pipeline writes internal_excluded (verified against a real run,
  // 2026-09-18) for articles dropped under the external-only rule.
  internal_excluded: "neutral",
  // Reviewer verdicts + finding kinds. "fine" is a positive result (checked
  // and correct), so it reads as success rather than neutral.
  fine: "success",
  wrong: "danger",
  outdated: "warning",
  missing_detail: "info",
  colour: "neutral",
  // Review desk work states — STATE-QUALIFIED because the bare `new` above is
  // the NEW-logo solid chip and means something else. Accepted is warning
  // (work owed), done is success, rejected is a quiet terminal state.
  review_new: "info",
  review_accepted: "warning",
  review_done: "success",
  review_rejected: "neutral",
  // Release-scan gap coverage.
  no_coverage: "danger",
  weak: "warning",

  // Default for anything we haven't classified yet
  default: "info",
};

const HUMAN_LABEL: Record<string, string> = {
  closed_won: "Closed (won)",
  closed_lost: "Closed (lost)",
  at_risk: "At risk",
  in_progress: "In progress",
  not_started: "Not started",
  not_applicable: "N/A",
  signed_off: "Signed off",
  // R-18: humanize() would give "Ok" and "Cancellation Not Honoured".
  ok: "OK",
  no_data: "No data",
  not_billing: "Not billing",
  count_delta: "Count delta",
  cancellation_not_honoured: "Not honoured",
  // Major Swag shipping: humanize() would give "In Transit" / "Label Created".
  in_transit: "In transit",
  label_created: "Label created",
  // Major Swag stock: the badge sits beside the item name, where "Low Stock"
  // would be redundant — the word "Low" is doing all the work.
  low_stock: "Low",
  out_of_stock: "Out",
  // Reads as "Unassigned" even though the key is no_department, because the
  // key had to stay distinct from commission coverage's `unassigned`.
  no_department: "Unassigned",
  // Integrations Monitor
  critical_resolved: "Critical (resolved)",
  on_time: "On time",
  insufficient_cadence_history: "No cadence history",
  no_timely_signal: "No timely signal",
  went_quiet: "Went quiet",
  step_change: "Step change",
  // Knowledge Base RAG
  ok_no_changes: "OK, no changes",
  incomplete_crawl: "Incomplete crawl",
  auth_failed: "Login failed",
  changed_pending: "Changed, re-index pending",
  pending_index: "Not indexed yet",
  deleted_pending_purge: "Deleted, purge pending",
  internal_excluded: "Internal (excluded)",
  missing_detail: "Missing detail",
  review_new: "New",
  review_accepted: "Accepted",
  review_done: "Done",
  review_rejected: "Rejected",
  no_coverage: "No article",
  weak: "Weak coverage",
};

function humanize(value: string): string {
  if (HUMAN_LABEL[value]) return HUMAN_LABEL[value];
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function StatusBadge({
  value,
  label,
  className,
}: {
  value: string;
  /** Override the displayed label. Defaults to a humanized version of `value`. */
  label?: string;
  className?: string;
}) {
  const variant = STATUS_MAP[value.toLowerCase()] ?? STATUS_MAP.default;
  return (
    <Badge variant={variant} className={className}>
      {label ?? humanize(value)}
    </Badge>
  );
}
